import React, { useState, useEffect } from "react";
import axios from "axios";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { useLocation } from "react-router-dom";
import { RingLoader } from "react-spinners";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
} from "@mui/material";
import { formatReadableAmount } from "../../../Common/FormatFunctions/FormatAmount";
import { FormaDateBalanceSheet } from "../../../Common/FormatFunctions/FormatDate";

const apikey = process.env.REACT_APP_API;

const SaleMonthWise = () => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const Company_Name = sessionStorage.getItem("Company_Name");
  const Company_GSTNO = sessionStorage.getItem("Company_GSTNO");
  const fromDate = searchParams.get("fromDate");
  const toDate = searchParams.get("toDate");
  const company_Code = searchParams.get("companyCode");
  const YearCode = searchParams.get("yearCode");
  const acCode = searchParams.get("acCode");

  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const API_URL = `${apikey}/MonthSaleWise_Register`;

  useEffect(() => {
    const fetchReportData = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await axios.get(API_URL, {
          params: {
            from_date: fromDate,
            to_date: toDate,
            acCode,
            Company_Code: company_Code,
            Year_code: YearCode,
          },
        });
        setReportData(response.data);
      } catch (error) {
        setError("Error fetching report");
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchReportData();
  }, [API_URL]);

  const handleExportToExcel = () => {
    const wb = XLSX.utils.book_new();

    const headers = [
      "Year",
      "Month",
      "SB Qty",
      "DN Qty",
      "CN Qty",
      "Net Qty",
      "SB Amt",
      "DN Amt",
      "CN Amt",
      "Net Amt",
    ];

    // Format data
    const formattedData = reportData.map((item) => ({
      Year: item.yr,
      Month: item.mn,
      "SB Qty": Number(item.SB_qntl) || 0,
      "DN Qty": Number(item.DN_qntl) || 0,
      "CN Qty": Number(item.CN_qntl) || 0,
      "Net Qty": Number(item.net_qntl) || 0,
      "SB Amt": Number(item.SB_amt) || 0,
      "DN Amt": Number(item.DN_amt) || 0,
      "CN Amt": Number(item.CN_amt) || 0,
      "Net Amt": Number(item.net_amt) || 0,
    }));

    // Grand total row
    const grandTotal = {
      Year: "",
      Month: "Grand Total",
      "SB Qty": reportData.reduce(
        (sum, r) => sum + (parseFloat(r.SB_qntl) || 0),
        0
      ),
      "DN Qty": reportData.reduce(
        (sum, r) => sum + (parseFloat(r.DN_qntl) || 0),
        0
      ),
      "CN Qty": reportData.reduce(
        (sum, r) => sum + (parseFloat(r.CN_qntl) || 0),
        0
      ),
      "Net Qty": reportData.reduce(
        (sum, r) => sum + (parseFloat(r.net_qntl) || 0),
        0
      ),
      "SB Amt": reportData.reduce(
        (sum, r) => sum + (parseFloat(r.SB_amt) || 0),
        0
      ),
      "DN Amt": reportData.reduce(
        (sum, r) => sum + (parseFloat(r.DN_amt) || 0),
        0
      ),
      "CN Amt": reportData.reduce(
        (sum, r) => sum + (parseFloat(r.CN_amt) || 0),
        0
      ),
      "Net Amt": reportData.reduce(
        (sum, r) => sum + (parseFloat(r.net_amt) || 0),
        0
      ),
    };
    formattedData.push(grandTotal);

    // Create sheet
    const ws = XLSX.utils.json_to_sheet(formattedData, {
      header: headers,
      origin: "A6",
    }); // data starts from row 6

    // Custom header rows (rows 1-5)
    const companyName = sessionStorage.getItem("Company_Name");
    const gst = sessionStorage.getItem("Company_GSTNO");
    const fromDateFormatted = FormaDateBalanceSheet(
      searchParams.get("fromDate")
    );
    const toDateFormatted = FormaDateBalanceSheet(searchParams.get("toDate"));
    const dateRange = `${fromDateFormatted} to ${toDateFormatted}`;

    const headerRows = [
      [`Company Name : ${companyName}`],
      [`GSTN         : ${gst}`],
      [`Report       : Sale Month Wise Register`],
      [`Period       : ${dateRange}`],
      [],
    ];

    XLSX.utils.sheet_add_aoa(ws, headerRows, { origin: "A1" }); // add header at top

    // Column widths
    const wsCols = [
      { wch: 10 },
      { wch: 10 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 18 },
      { wch: 15 },
      { wch: 15 },
      { wch: 18 },
    ];
    ws["!cols"] = wsCols;

    // Finalize
    XLSX.utils.book_append_sheet(wb, ws, "SaleMonthWise");
    XLSX.writeFile(wb, "SaleMonthWise.xlsx");
  };

  const handlePrint = () => {
    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text(Company_Name, doc.internal.pageSize.width / 2, 10, {
      align: "center",
    });
    doc.setFontSize(10);
    doc.text(
      `Sale Month Wise Register From: ${FormaDateBalanceSheet(
        fromDate
      )} To: ${FormaDateBalanceSheet(toDate)}`,
      10,
      20
    );

    const table = [
      [
        "Year",
        "Month",
        "SB Qty",
        "DN Qty",
        "CN Qty",
        "Net Qty",
        "SB Amt",
        "DN Amt",
        "CN Amt",
        "Net Amt",
      ],
    ];
    let totals = {
      SB_qntl: 0,
      DN_qntl: 0,
      CN_qntl: 0,
      net_qntl: 0,
      SB_amt: 0,
      DN_amt: 0,
      CN_amt: 0,
      net_amt: 0,
    };

    reportData.forEach((item) => {
      table.push([
        item.yr,
        item.mn,
        formatReadableAmount(item.SB_qntl),
        formatReadableAmount(item.DN_qntl),
        formatReadableAmount(item.CN_qntl),
        formatReadableAmount(item.net_qntl),
        formatReadableAmount(item.SB_amt),
        formatReadableAmount(item.DN_amt),
        formatReadableAmount(item.CN_amt),
        formatReadableAmount(item.net_amt),
      ]);
      Object.keys(totals).forEach((key) => {
        totals[key] += parseFloat(item[key]) || 0;
      });
    });

    table.push([
      "",
      "Grand Total",
      formatReadableAmount(totals.SB_qntl),
      formatReadableAmount(totals.DN_qntl),
      formatReadableAmount(totals.CN_qntl),
      formatReadableAmount(totals.net_qntl),
      formatReadableAmount(totals.SB_amt),
      formatReadableAmount(totals.DN_amt),
      formatReadableAmount(totals.CN_amt),
      formatReadableAmount(totals.net_amt),
    ]);

    doc.autoTable({
      head: [table[0]],
      body: table.slice(1),
      startY: 25,
      styles: {
        fontSize: 8,
        halign: "right",
        cellPadding: 2,
      },
      columnStyles: {
        0: { halign: "center" }, // Year
        1: { halign: "center" }, // Month
      },
      headStyles: {
        fillColor: [200, 200, 200],
        halign: "center",
        fontStyle: "bold",
      },
      didParseCell: (data) => {
        if (data.row.index === table.length - 2) {
          // last row (total)
          data.cell.styles.fillColor = [255, 255, 153]; // light yellow
          data.cell.styles.fontStyle = "bold";
        }
      },
      theme: "grid",
    });

    const blob = doc.output("blob");
    const url = URL.createObjectURL(blob);
    const win = window.open(url);
    setTimeout(() => win && win.print(), 1000);
  };

  const total = (key) =>
    reportData
      .reduce((sum, row) => sum + (parseFloat(row[key]) || 0), 0)
      .toFixed(2);

  return (
    <div style={{marginTop:"-80px"}}>
      {/* <Typography align="center" fontWeight="bold" fontSize={20}>
        {Company_Name}
      </Typography>
      <Typography
        align="center"
        fontSize={14}
        sx={{ textDecoration: "underline" }}
      >
        GSTN : {Company_GSTNO}
      </Typography> */}
      <Typography align="center" fontWeight="bold" fontSize={18}>
        Sale Month Wise Register
      </Typography>
      <Typography align="center" fontSize={14}>
        {FormaDateBalanceSheet(fromDate)} to {FormaDateBalanceSheet(toDate)}
      </Typography>

      <div className="mb-3 text-center">
        <button className="btn btn-secondary me-2" onClick={handlePrint}>
          Print
        </button>
        <button className="btn btn-success" onClick={handleExportToExcel}>
          Export to Excel
        </button>
      </div>

      <TableContainer
        component={Paper}
        sx={{ maxWidth: 1100, margin: "auto", marginBottom: 5 }}
      >
        <Table size="small" id="reportTable">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f0f0f0" }}>
              <TableCell align="center">Year</TableCell>
              <TableCell align="center">Month</TableCell>
              <TableCell align="right">SB Qty</TableCell>
              <TableCell align="right">DN Qty</TableCell>
              <TableCell align="right">CN Qty</TableCell>
              <TableCell align="right">Net Qty</TableCell>
              <TableCell align="right">SB Amt</TableCell>
              <TableCell align="right">DN Amt</TableCell>
              <TableCell align="right">CN Amt</TableCell>
              <TableCell align="right">Net Amt</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reportData.map((item, idx) => (
              <TableRow key={idx}>
                <TableCell align="center">{item.yr}</TableCell>
                <TableCell align="center">{item.mn}</TableCell>
                <TableCell align="right">
                  {formatReadableAmount(item.SB_qntl)}
                </TableCell>
                <TableCell align="right">
                  {formatReadableAmount(item.DN_qntl)}
                </TableCell>
                <TableCell align="right">
                  {formatReadableAmount(item.CN_qntl)}
                </TableCell>
                <TableCell align="right">
                  {formatReadableAmount(item.net_qntl)}
                </TableCell>
                <TableCell align="right">
                  {formatReadableAmount(item.SB_amt)}
                </TableCell>
                <TableCell align="right">
                  {formatReadableAmount(item.DN_amt)}
                </TableCell>
                <TableCell align="right">
                  {formatReadableAmount(item.CN_amt)}
                </TableCell>
                <TableCell align="right">
                  {formatReadableAmount(item.net_amt)}
                </TableCell>
              </TableRow>
            ))}
            <TableRow sx={{ backgroundColor: "#ffff99" }}>
              <TableCell colSpan={2} align="right">
                <b>Grand Total</b>
              </TableCell>
              <TableCell align="right">
                <b>{formatReadableAmount(total("SB_qntl"))}</b>
              </TableCell>
              <TableCell align="right">
                <b>{formatReadableAmount(total("DN_qntl"))}</b>
              </TableCell>
              <TableCell align="right">
                <b>{formatReadableAmount(total("CN_qntl"))}</b>
              </TableCell>
              <TableCell align="right">
                <b>{formatReadableAmount(total("net_qntl"))}</b>
              </TableCell>
              <TableCell align="right">
                <b>{formatReadableAmount(total("SB_amt"))}</b>
              </TableCell>
              <TableCell align="right">
                <b>{formatReadableAmount(total("DN_amt"))}</b>
              </TableCell>
              <TableCell align="right">
                <b>{formatReadableAmount(total("CN_amt"))}</b>
              </TableCell>
              <TableCell align="right">
                <b>{formatReadableAmount(total("net_amt"))}</b>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {loading && (
        <div
          style={{
            position: "fixed",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            zIndex: 9999,
          }}
        >
          <RingLoader size={80} />
        </div>
      )}
      {error && <div className="alert alert-danger text-center">{error}</div>}
    </div>
  );
};

export default SaleMonthWise;
